import type { Member } from "../../../domain/app-types.ts";
import type { Category, Todo } from "../../../domain/entities.ts";
import { activeTags } from "../selectors.ts";
import { validTimelineDate } from "../collection-timeline.ts";
import { normalizeReminders } from "../reminders.ts";
import { reminderValidationMessage } from "../draft-validation.ts";
import type { TableColumn } from "./preferences.ts";
import { isCustomColumn } from "./preferences.ts";
import { customFieldValue } from "../../custom-fields/model.ts";

export type TablePatch = Partial<Pick<Todo, "priority" | "startDate" | "dueDate" | "assignedTo" | "tags" | "tagOrder" | "customFields" | "customFieldSchema">>;

export function tableEditSnapshot(task: Todo, field: TableColumn): TablePatch {
  if (isCustomColumn(field)) { const id = field.slice(6); return { customFields: { [id]: task.customFields?.[id] ?? null }, customFieldSchema: task.customFieldSchema?.[id] ? { [id]: task.customFieldSchema[id] } : {} }; }
  if (field === "priority") return { priority: task.priority };
  if (field === "start") return { startDate: task.startDate || null };
  if (field === "due") return { dueDate: task.dueDate || null };
  if (field === "assignee") return { assignedTo: [...(task.assignedTo || [])] };
  if (field === "tags") return { tags: { ...(task.tags || {}) }, tagOrder: [...(task.tagOrder || [])] };
  throw new Error("Open task details to edit this field.");
}

export function sameTableEdit(task: Todo, snapshot: TablePatch, field: TableColumn): boolean {
  return JSON.stringify(tableEditSnapshot(task, field)) === JSON.stringify(snapshot);
}

export function validateTableDates(task: Todo): void {
  if (task.startDate && task.dueDate && task.startDate > task.dueDate) throw new Error("Start date must not be after the due date. Open task details to adjust both dates.");
  const reminders = normalizeReminders(task);
  if (reminders.some(reminder => reminder.mode === "relative") && !task.startAt && !task.dueDate) throw new Error("Relative reminders require a start time or due date. Open task details to adjust reminders first.");
  for (const reminder of reminders) {
    if (reminder.mode !== "absolute" || !reminder.at) continue;
    const message = reminderValidationMessage(task.dueDate || "", reminder.at);
    if (message) throw new Error(`${message} Open task details to adjust reminders.`);
  }
}

export function tableEditPatch(task: Todo, field: TableColumn, value: string | string[], members: Member[], category?: Category, definition?: import("../../../domain/entities.ts").CustomFieldDefinition): TablePatch {
  if (isCustomColumn(field)) {
    if (!definition || definition.archived || !category?.customFieldIds?.includes(definition.id)) throw new Error("This field is no longer available for editing.");
    return { customFields: { [definition.id]: customFieldValue(definition, value) }, customFieldSchema: { [definition.id]: structuredClone(definition) } };
  }
  if (field === "priority") {
    if (!["none", "low", "medium", "high", "urgent"].includes(String(value))) throw new Error("Choose a valid priority.");
    return { priority: value as Todo["priority"] };
  }
  if (field === "start" || field === "due") {
    const date = String(value);
    if (date && !validTimelineDate(date)) throw new Error("Choose a valid date.");
    const patch = field === "start" ? { startDate: date || null } : { dueDate: date || null };
    validateTableDates({ ...task, ...patch });
    return patch;
  }
  if (field === "assignee") {
    const selected = [...new Set(Array.isArray(value) ? value : [])];
    if (selected.some(id => !members.some(member => member.id === id) && !task.assignedTo?.includes(id))) throw new Error("An assignee is no longer available. Reopen the editor to refresh people.");
    return { assignedTo: selected };
  }
  if (field === "tags") {
    const columns = category?.boardColumns || [];
    const currentColumn = activeTags(task)[0];
    const tags = [...new Set(String(value).split(",").map(tag => tag.trim().replace(/^#/, "").toLowerCase()).filter(Boolean))];
    if (tags.length > 100 || tags.some(tag => tag.length > 120)) throw new Error("Use at most 100 tags, each up to 120 characters.");
    const order = [...(columns.includes(currentColumn) ? [currentColumn] : []), ...tags.filter(tag => !columns.includes(tag))];
    return { tags: Object.fromEntries([...new Set([...Object.keys(task.tags || {}), ...order])].map(tag => [tag, order.includes(tag)])), tagOrder: order };
  }
  throw new Error("Open task details to edit this field.");
}

export function applyTablePatch(task: Todo, patch: TablePatch, field: TableColumn): void {
  if (!isCustomColumn(field)) { Object.assign(task, patch); return; }
  const id = field.slice(6); task.customFields ||= {}; task.customFieldSchema ||= {};
  task.customFields[id] = patch.customFields?.[id] ?? null;
  if (patch.customFieldSchema?.[id]) task.customFieldSchema[id] = structuredClone(patch.customFieldSchema[id]);
}
